<div class="chat-header">
  <div class="chat-title-group">
    <mat-icon class="chat-ai-icon">auto_awesome</mat-icon>
    <span class="chat-title" translate>Unite Assistant</span>
    <span class="preview-badge" translate>Preview</span>
  </div>
  <span class="spacer"></span>
  @if (!showHistory && chatService.otherPendingRuns().length > 0) {
    <button mat-icon-button [matTooltip]="'Documents for your attention' | translate" (click)="showPendingRuns()"
      [matBadge]="chatService.otherPendingRuns().length" matBadgeColor="warn" matBadgeSize="small">
      <mat-icon>upload_file</mat-icon>
    </button>
  }
  @if (!showHistory && chatService.messages().length > 0) {
    <button mat-icon-button [matTooltip]="'New conversation' | translate" (click)="newConversation()">
      <mat-icon>edit_note</mat-icon>
    </button>
  }
  <button mat-icon-button [matTooltip]="(showHistory ? 'Back' : 'Recent conversations') | translate" (click)="toggleHistory()">
    <mat-icon>{{ showHistory ? 'arrow_back' : 'history' }}</mat-icon>
  </button>
  <button mat-icon-button (click)="closeClicked.emit()" [attr.aria-label]="'Close' | translate">
    <mat-icon>close</mat-icon>
  </button>
</div>

<input #fileInput type="file" class="hidden-file-input" (change)="onFileSelected($event)" />

<!-- Documents the assistant has prepared and waits on an answer for, or handled without a question and nobody has
     looked at yet (other than the conversation on screen). -->
<ng-template #pendingRunList>
  <div class="w-full mb-4">
    <div class="history-header">
      <span translate>For your attention</span>
    </div>
    @for (run of chatService.otherPendingRuns(); track run.runId) {
      <button class="conversation-item" (click)="openPendingRun(run)">
        <mat-icon class="conv-icon">{{ run.state === 'finished' ? 'task' : run.state === 'running' ? 'hourglass_top' : run.source === 'upload' ? 'upload_file' : 'chat_bubble_outline' }}</mat-icon>
        <div class="conv-details">
          <span class="conv-title">{{ run.title || ('Conversation' | translate) }}</span>
          <span class="conv-date">
            {{ (run.state === 'finished' ? 'Finished' : run.state === 'running' ? 'In progress' : 'Waiting for your answer') | translate }} ·
            {{ run.updatedAt | date: 'mediumDate' }} · {{ run.updatedAt | date: 'shortTime' }}
          </span>
        </div>
      </button>
    }
  </div>
</ng-template>

@if (showHistory) {
  <div class="history-panel">
    @if (chatService.otherPendingRuns().length > 0) {
      <ng-container *ngTemplateOutlet="pendingRunList"></ng-container>
    }
    <div class="history-header">
      <span translate>Recent conversations</span>
    </div>
    @if (chatService.isLoadingConversations()) {
      <div class="history-loading">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
    } @else if (chatService.conversations().length === 0) {
      <div class="history-empty">
        <mat-icon>chat_bubble_outline</mat-icon>
        <p translate>No conversations yet</p>
      </div>
    } @else {
      <div class="history-list">
        @for (conv of chatService.conversations(); track conv.id) {
          <button class="conversation-item" (click)="selectConversation(conv.id)">
            <mat-icon class="conv-icon">chat_bubble_outline</mat-icon>
            <div class="conv-details">
              <span class="conv-title">{{ conv.title || ('Conversation' | translate) }}</span>
              <span class="conv-date">{{ conv.updatedAt | date: 'mediumDate' }} · {{ conv.updatedAt | date: 'shortTime' }}</span>
            </div>
            <span
              class="conv-delete"
              role="button"
              tabindex="0"
              [matTooltip]="'Delete conversation' | translate"
              [attr.aria-label]="'Delete conversation' | translate"
              (click)="deleteConversation($event, conv.id)"
              (keydown.enter)="deleteConversation($event, conv.id)">
              <mat-icon>delete_outline</mat-icon>
            </span>
          </button>
        }
      </div>
    }
  </div>
} @else {
<div class="messages-container" #messagesContainer>
  @if (chatService.isLoadingConversation()) {
    <div class="history-loading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else if (chatService.messages().length === 0) {
    <div class="empty-state">
      <mat-icon>support_agent</mat-icon>
      <p>
        @if (firstName) {
          {{ 'Hi {{name}}, how can I help you today?' | translate: { name: firstName } }}
        } @else {
          {{ 'How can I help you today?' | translate }}
        }
      </p>
    </div>
  }

  @for (message of chatService.messages(); track message.id) {
    <div class="message" [class.user-message]="message.role === 'user'" [class.assistant-message]="message.role === 'assistant'">
      @if (message.role === 'assistant' && showAssistantBubble(message)) {
        <div class="message-icon assistant-icon">
          <mat-icon>support_agent</mat-icon>
        </div>
        <div class="assistant-content">
          <div class="message-bubble" [innerHTML]="message.content | markdown" (click)="onMessageBubbleClick($event)"></div>
          @if (message.appLinks?.length) {
            <div class="app-links">
              @for (link of message.appLinks!; track link.url) {
                <a mat-stroked-button [routerLink]="link.routerCommands" [queryParams]="link.queryParams" class="app-link-button">
                  <mat-icon>open_in_new</mat-icon>{{ link.label }}
                </a>
              }
            </div>
          }
        </div>
      } @else if (message.role === 'user') {
        <div class="message-content">
          <div class="message-bubble">{{ message.content }}@if (message.isStreaming) {<span class="cursor">▋</span>}</div>
          @if (message.attachmentNames?.length) {
            <div class="message-attachments">
              @for (name of message.attachmentNames!; track name) {
                <span class="attachment-tag"><mat-icon>attach_file</mat-icon>{{ name }}</span>
              }
            </div>
          }
        </div>
        <div class="message-icon user-icon">
          <mat-icon>person</mat-icon>
        </div>
      }
    </div>
  }

  @if (showTypingIndicator) {
    <div class="message assistant-message">
      <div class="message-icon assistant-icon">
        <mat-icon>support_agent</mat-icon>
      </div>
      <div class="message-bubble typing">
        <span></span><span></span><span></span>
      </div>
    </div>
  }

  @if (chatService.pendingApproval(); as approval) {
    <div class="approval-card">
      <div class="approval-header">
        <mat-icon class="approval-icon">build</mat-icon>
        <span translate>Tool approval required</span>
      </div>
      <p class="approval-description">
        <span translate>The assistant wants to call</span>
        <strong> {{ approval.toolName }} </strong>
        <span translate>on</span>
        <strong> {{ approval.serverLabel }}</strong>
      </p>
      @if (approval.arguments && approval.arguments !== '{}') {
        <pre class="approval-args">{{ approval.arguments }}</pre>
      }
      <div class="approval-actions">
        <button mat-flat-button color="primary" (click)="chatService.submitApproval(true)">
          <mat-icon>check</mat-icon>{{ 'Allow' | translate }}
        </button>
        <button mat-stroked-button (click)="chatService.submitApproval(false)">
          <mat-icon>close</mat-icon>{{ 'Deny' | translate }}
        </button>
      </div>
    </div>
  }

  @if (chatService.pendingOAuthConsent(); as consent) {
    <div class="approval-card">
      <div class="approval-header">
        <mat-icon class="approval-icon">lock</mat-icon>
        <span translate>Authorization required</span>
      </div>
      <p class="approval-description">
        <span translate>The assistant needs access to</span>
        <strong> {{ consent.provider }}</strong>
      </p>
      @if (consent.scopes?.length) {
        <ul class="consent-scopes">
          @for (scope of consent.scopes!; track scope) {
            <li>{{ scope }}</li>
          }
        </ul>
      }
      <div class="approval-actions">
        @if (chatService.oauthPopupInProgress()) {
          <span class="oauth-waiting"><mat-icon>hourglass_top</mat-icon>{{ 'Waiting for authorization in popup…' | translate }}</span>
        } @else if (!oauthWindowOpened) {
          <button mat-flat-button color="primary" (click)="openOAuthWindow(consent.authorizationUrl)">
            <mat-icon>open_in_new</mat-icon>{{ 'Authorize' | translate }}
          </button>
        } @else {
          <button mat-flat-button color="primary" (click)="continueAfterOAuth()">
            <mat-icon>check</mat-icon>{{ 'Done, continue' | translate }}
          </button>
        }
        <button mat-stroked-button (click)="oauthWindowOpened = false; chatService.submitOAuthConsent(false)">
          <mat-icon>close</mat-icon>{{ 'Cancel' | translate }}
        </button>
      </div>
    </div>
  }

  @if (chatService.pendingQuestion(); as question) {
    <div class="approval-card question-card">
      <div class="approval-description question-text" [innerHTML]="question.question | markdown" (click)="onMessageBubbleClick($event)"></div>
      <div class="approval-actions">
        @for (option of question.options; track option.value; let first = $first) {
          @if (first) {
            <button mat-flat-button color="primary" (click)="chatService.answerQuestion(option.value)">
              {{ option.label }}
            </button>
          } @else {
            <button mat-stroked-button (click)="chatService.answerQuestion(option.value)">
              {{ option.label }}
            </button>
          }
        }
        @if (question.allowOther) {
          <button mat-stroked-button (click)="answerOther()">
            {{ question.otherLabel || ('Something else…' | translate) }}
          </button>
        }
      </div>
    </div>
  }

  @if (chatService.hasError()) {
    <div class="error-message">
      <mat-icon>error_outline</mat-icon>
      <span translate>Could not reach the assistant. Please try again.</span>
    </div>
  }
</div>

@if (chatService.pendingAttachments().length > 0) {
  <div class="attachments-area">
    @for (attachment of chatService.pendingAttachments(); track attachment.fileId) {
      <mat-chip-set>
        <mat-chip (removed)="chatService.removeAttachment(attachment.fileId)">
          <mat-icon matChipAvatar>attach_file</mat-icon>
          {{ attachment.fileName }}
          <button matChipRemove [attr.aria-label]="'Remove attachment' | translate">
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip>
      </mat-chip-set>
    }
  </div>
}

<div class="input-area">
  <button
    mat-icon-button
    class="attach-button"
    (click)="attachFile()"
    [disabled]="chatService.isStreaming() || chatService.isUploading()"
    [attr.aria-label]="'Attach file' | translate"
    [matTooltip]="'Attach file' | translate">
    @if (chatService.isUploading()) {
      <mat-spinner diameter="20"></mat-spinner>
    } @else {
      <mat-icon>attach_file</mat-icon>
    }
  </button>
  <mat-form-field appearance="outline" class="input-field">
    <textarea
      #messageInput
      matInput
      cdkTextareaAutosize
      cdkAutosizeMinRows="1"
      cdkAutosizeMaxRows="6"
      [(ngModel)]="inputText"
      (keydown.enter)="onEnterKey($event)"
      [placeholder]="(chatService.activeRunWorking() ? 'The assistant is working on this document…' : 'Type your message…') | translate"
      [disabled]="chatService.isStreaming() || chatService.activeRunWorking() || !!chatService.pendingApproval() || !!chatService.pendingOAuthConsent()"
      autocomplete="off"></textarea>
  </mat-form-field>
  @if (chatService.isStreaming()) {
    <button mat-icon-button class="send-button" (click)="chatService.stopStreaming()" [attr.aria-label]="'Stop' | translate">
      <mat-icon>stop_circle</mat-icon>
    </button>
  } @else {
    <button mat-icon-button class="send-button" (click)="sendMessage()" [disabled]="!inputText.trim() || chatService.activeRunWorking() || !!chatService.pendingApproval() || !!chatService.pendingOAuthConsent()" [attr.aria-label]="'Send' | translate">
      <mat-icon>send</mat-icon>
    </button>
  }
</div>
}
